import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  addAsk, createCustomerRequest, deleteCustomerRequest, removeAsk, setCustomerRequestStatus, updateCustomerRequest,
} from '../api';
import { customerRequestKeys } from './useCustomerRequests';
import type {
  AddAskInput, CreateCustomerRequestInput, CustomerRequestDetail, CustomerRequestStatus, UpdateCustomerRequestInput,
} from '../types';

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const data = (e as { data?: { error?: string } }).data;
    if (data?.error) return data.error;
  }
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message);
  return 'Something went wrong';
}

/** Every mutation returns the fresh detail: seed it, then refresh the lists. */
function useRefresh() {
  const qc = useQueryClient();
  return (detail?: CustomerRequestDetail) => {
    if (detail) qc.setQueryData(customerRequestKeys.detail(detail.id), detail);
    qc.invalidateQueries({ queryKey: customerRequestKeys.all });
  };
}

export function useCreateCustomerRequest() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (data: CreateCustomerRequestInput) => createCustomerRequest(data),
    onSuccess: (detail) => { refresh(detail); toast.success('Request created'); },
    onError: (e) => toast.error('Failed to create the request', { description: errMsg(e) }),
  });
}

export function useUpdateCustomerRequest() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { id: string; data: UpdateCustomerRequestInput }) => updateCustomerRequest(args.id, args.data),
    onSuccess: (detail) => refresh(detail),
    onError: (e) => toast.error('Failed to save', { description: errMsg(e) }),
  });
}

export function useAddAsk() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { id: string; data: AddAskInput }) => addAsk(args.id, args.data),
    onSuccess: (detail) => { refresh(detail); toast.success('Vote recorded'); },
    onError: (e) => toast.error('Failed to record the vote', { description: errMsg(e) }),
  });
}

export function useRemoveAsk() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { id: string; askId: string }) => removeAsk(args.id, args.askId),
    onSuccess: (detail) => refresh(detail),
    onError: (e) => toast.error('Failed to remove the vote', { description: errMsg(e) }),
  });
}

export function useSetCustomerRequestStatus() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { id: string; status: CustomerRequestStatus; note?: string }) =>
      setCustomerRequestStatus(args.id, args.status, args.note),
    onSuccess: ({ request, notifications }) => {
      refresh(request);
      // Only claim people were told when SendGrid accepted their email.
      const n = notifications.recipients.length;
      const people = (k: number) => `${k} ${k === 1 ? 'person' : 'people'}`;
      if (notifications.sent) toast.success(`Status updated, ${people(notifications.count ?? 0)} emailed`);
      else if (!n) toast.success('Status updated, no one to notify');
      else if (notifications.reason === 'disabled') toast.success(`Status updated. Emails are off, so nobody was emailed (${people(n)} would have been).`);
      else toast.warning(`Status updated, but the email to ${people(n)} failed`);
    },
    onError: (e) => toast.error('Failed to change the status', { description: errMsg(e) }),
  });
}

export function useDeleteCustomerRequest() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => deleteCustomerRequest(id),
    onSuccess: () => { refresh(); toast.success('Request deleted'); },
    onError: (e) => toast.error('Failed to delete the request', { description: errMsg(e) }),
  });
}
